/* Settings ▸ Permit Library ▸ Permit Row Colors (2026-10-21; prototype
 * docs/prototypes/Permit-Row-Colors-Prototype-v3-Rules.html; BO-UI Standard v4 applied the same day).
 *
 * The lab's colour rules for the result grids. Each rule = a colour + one to three
 * conditions joined by AND, each "Result <operator> any | all of <limit types>":
 *     Pink    If result ≥ MCL, SMCL      (SMCL added 2026-10-23)
 *     Orange  If result ≥ MRL, AL, SMAL, < MCL   (SMCL removed 2026-10-23)
 *     Yellow  If result > MDL, < (MRL, AL, SMCL, MCL)
 * Rules are checked from the top; the first one a result meets colours the row.
 *
 * BO-UI Standard v4 — Settings BO (Class A), List View anatomy (§4):
 *   breadcrumb · <FormTitle> with the load-time and view-only Audit chips (no sub-caption, no
 *   standing hint panel — §4 / §5) · <AppToolbar> New · Edit · Delete · Refresh · Export to, then
 *   this form's Move Up · Move Down (owner request: after the standard set; there is no Restore
 *   Defaults — hidden 2026-10-23, then removed with its API endpoint and SQL procedure 2026-10-24:
 *   the lab creates its rules at runtime, nothing seeds them) ·
 *   the shared fuzzy <GridSearch> on its own row, left-aligned (§9) · <AppDataGrid> with the
 *   checkbox column, the data columns and the one standard footer, full width · BELOW it the
 *   Preview <SectionPanel> laid out exactly as on the New / Edit page and DRIVEN BY THE TICKED RULE
 *   (owner request 2026-10-23: that rule alone, example rows + limit boxes built from the limits it
 *   names; nothing ticked = every active rule in order) — its grid with the example
 *   permit + Try a result in a column beside it (owner request 2026-10-23; until then the Preview
 *   sat beside the rules grid, both stretched to one height) · the Preview grid
 *   whose grid is painted with each rule's OWN colour through the same generated classes the result
 *   forms use; its 7 example rows need no pager, so it has no footer (owner request). Neither grid is
 *   zebra-striped (owner request 2026-10-23): on this page a row's background IS the data — a
 *   striped row in the Preview would read as a colour rule — so .prc-nozebra sets the shared
 *   grid's own --adg-row-even token to the odd-row colour for these two grids only.
 * New / Edit / a double-click REPLACE the list with the full-page detail
 * (PermitRowColorDetail — standing rule 12, never a popup). Delete through useDeleteFlow; Move
 * through the shared confirm. The server is authoritative for every rule. After
 * every change the rules are applied at once to the result grids of this session. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormTitle } from '../../../components/FormTitle';
import { AppToolbar } from '../../../components/AppToolbar';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { GridSearch } from '../../../components/GridSearch';
import { SectionPanel } from '../../../components/SectionPanel';
import { useConfirm, useDeleteFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { fuzzyFilter } from '../../../utils/fuzzyMatch';
import type { RowColorRule } from '../../analysis/permitRowColorState';
import { applyPermitRowColors, fetchPermitRowColors, type RowColorSet } from '../../analysis/permitRowColors';
import {
  AUDIT_FORM, PREVIEW_COLUMNS, PREVIEW_PAGE_SIZE, PermitRowColorDetail, previewRowClass, previewRuleAlone,
  previewRows, type AfterSave,
} from './PermitRowColorDetail';
import { ExamplePermitControls } from './ExamplePermitControls';
import { DEFAULT_EXAMPLE, DEFAULT_TRY_RESULT, exampleFieldsFor, type ExamplePermit } from './permitExample';
import { deleteRowColorRule, moveRowColorRule, rowColorRefusal } from './permitRowColorsApi';
import './permitRowColors.css';

export { AUDIT_FORM };
export const PAGE_ID = 'pln-permit-row-colors';

/** The open detail: a rule id, or null for New. `n` re-opens a fresh New after Save and New. */
interface OpenDetail { id: string | null; n: number }

/** The data columns — shared by the grid and the export (the export carries no row icon). */
const DATA_COLUMNS: AppColumn<RowColorRule>[] = [
  // Narrow and pinned (owner request 2026-10-21): just the position number; no filter chevron.
  { field: 'sortOrder', header: 'Order', width: 90, minWidth: 90, maxWidth: 90, align: 'center', type: 'number', filterable: false },
  {
    field: 'color', header: 'Colour', width: 84, align: 'center', filterable: false,
    text: (r) => r.color,
    render: (r) => <span className="prc-swatch" style={{ background: r.color }} title={r.color} aria-label={`Colour ${r.color}`} />,
  },
  { field: 'name', header: 'Name', minWidth: 180 },
  { field: 'text', header: 'Rule', minWidth: 320, wrap: true, text: (r) => r.text },
  {
    field: 'isActive', header: 'Active', width: 76, align: 'center',
    text: (r) => (r.isActive ? 'Yes' : 'No'),
    // The same toggle switch as the New / Edit page (owner request 2026-10-23), display-only here:
    // the state is changed on the rule's own page, through the confirmed, audited Save.
    render: (r) => (
      <span className={`prc-switch${r.isActive ? ' on' : ''}`} role="img" aria-label={r.isActive ? 'Active' : 'Inactive'}
        title={r.isActive ? 'Active — open the rule to switch it off' : 'Inactive — open the rule to switch it on'} />
    ),
  },
];

/** What the fuzzy search reads for a rule — every column the grid shows. */
const searchText = (r: RowColorRule) => `${r.sortOrder} ${r.name} ${r.text} ${r.color} ${r.isActive ? 'Yes' : 'No'}`;

export default function PermitRowColorsPage({ onHome }: { onHome?: () => void }) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const runDelete = useDeleteFlow();
  const perm = useNavActions(PAGE_ID);
  const [set, setSet] = useState<RowColorSet | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | undefined>();
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<OpenDetail | null>(null);
  const [tryValue, setTryValue] = useState(DEFAULT_TRY_RESULT);
  // The example permit the Preview colours against — editable, so the lab can try its own limits.
  const [example, setExample] = useState<ExamplePermit>(DEFAULT_EXAMPLE);

  const rules = useMemo(() => set?.rules ?? [], [set]);
  const shown = useMemo(() => fuzzyFilter(rules, search, searchText), [rules, search]);

  const load = useCallback(async (quiet = false): Promise<RowColorSet | null> => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const next = await fetchPermitRowColors();
      setSet(next);
      applyPermitRowColors(next);   // result grids of this session (and the preview) pick it up at once
      setSelected((sel) => new Set([...sel].filter((id) => next.rules.some((r) => r.id === id))));
      if (!quiet) setLoadMs(Math.round(performance.now() - t0));
      return next;
    } catch (e) {
      notify.error(rowColorRefusal(e) ?? 'Could not load the permit row colours. Please retry.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const selectedRule = useMemo(
    () => (selected.size === 1 ? rules.find((r) => r.id === [...selected][0]) : undefined), [selected, rules]);

  const openDetail = (id: string | null) => setDetail((d) => ({ id, n: (d?.n ?? 0) + 1 }));

  async function afterSave(saved: RowColorRule | undefined, after: AfterSave) {
    const next = await load(true);
    if (after === 'close') { setDetail(null); return; }
    if (after === 'new') { openDetail(null); return; }
    const id = saved?.id ?? detail?.id ?? null;
    // Stay on the saved rule; if it cannot be read back, return to the list rather than show a blank New.
    if (id && next?.rules.some((r) => r.id === id)) setDetail((d) => ({ id, n: d?.id === id ? d.n : (d?.n ?? 0) + 1 }));
    else setDetail(null);
  }

  // ── list actions ──────────────────────────────────────────────────────────
  async function requestDelete(rule: RowColorRule) {
    await runDelete({
      formName: AUDIT_FORM,
      message: `Delete the colour rule “${rule.name}”? Rows it coloured go back to their normal colour.`,
      delete: async () => { await deleteRowColorRule(rule.id); },
      onDeleted: () => { setSelected(new Set()); void load(true); },
      onError: (e) => notify.error(rowColorRefusal(e) ?? 'The colour rule could not be deleted.'),
    });
  }

  async function move(rule: RowColorRule, direction: -1 | 1) {
    const ok = await confirm({
      title: 'Change the order',
      message: `Move “${rule.name}” ${direction < 0 ? 'up' : 'down'} to position ${rule.sortOrder + direction}? `
        + 'Rules are checked from the top, so the order decides which colour wins.',
      confirmLabel: direction < 0 ? 'Move Up' : 'Move Down',
    });
    if (!ok) return;
    try {
      await moveRowColorRule(rule.id, direction);
      notify.success('Order changed successfully.');
      await load(true);
    } catch (e) {
      notify.error(rowColorRefusal(e) ?? 'The order could not be changed.');
    }
  }

  // ── detail (replaces the list) ────────────────────────────────────────────
  const detailRule = detail?.id ? rules.find((r) => r.id === detail.id) : undefined;
  if (detail && set && (detail.id === null || detailRule)) {
    return (
      <PermitRowColorDetail
        key={`${detail.id ?? 'new'}-${detail.n}`}
        rule={detailRule}
        set={set}
        orderedIds={shown.map((r) => r.id)}
        onHome={onHome}
        onClose={() => setDetail(null)}
        onNavigate={(id) => openDetail(id)}
        onSaved={(saved, after) => void afterSave(saved, after)}
      />
    );
  }

  // ── grid ──────────────────────────────────────────────────────────────────
  const canWrite = perm.write !== false;
  // Owner request 2026-10-21: no row edit icon — a rule opens from Edit, a double-click or Enter.
  const columns = DATA_COLUMNS;
  const exp = gridExportMatrix(shown, DATA_COLUMNS);

  function onSelection(ids: Set<string | number>) {
    // One rule at a time — Edit / Delete / Move act on the single selected row.
    const next = [...ids].filter((id) => !selected.has(id));
    setSelected(new Set(next.length ? [next[next.length - 1]!] : []));
  }

  // The Preview follows the rule ticked in the grid (owner request 2026-10-23): that rule on its
  // own, with example rows and limit boxes built from the limits IT names. Nothing ticked → every
  // active rule in its order, which is how the result forms colour a row.
  const previewSet = selectedRule ? previewRuleAlone(selectedRule) : rules.filter((r) => r.isActive);
  const previewFields = exampleFieldsFor(previewSet);
  const pageRows = previewRows(previewSet, previewFields, example);
  const previewNote = selectedRule
    ? `${selectedRule.name} only`
    : 'All active rules, in order — tick a rule to preview it on its own';

  return (
    <div className="bl-page mlv-host prc-page">
      {/* Heading block — the same shared bl-top / dp-page-hdr as Settings ▸ Default Settings ▸ Date Filter
          (owner request 2026-10-21: "keep the form heading space like this"). */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span>Permit Library</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Permit Row Colors</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId={PAGE_ID} auditFormName={AUDIT_FORM} loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div className="mlv-toolbar-row">
          {/* Owner request 2026-10-21: the standard New · Edit · Delete · Refresh · Export to come FIRST,
              then this form's own Move Up · Move Down — the toolbar's existing `right` slot (rendered
              after Export to), not a per-form reorder of the shared buttons. No Restore Defaults
              (removed 2026-10-24, owner request). */}
          <AppToolbar
            right={(
              <>
                <button type="button" className="ca-btn" disabled={!canWrite || !selectedRule || selectedRule.sortOrder === 1}
                  onClick={() => selectedRule && void move(selectedRule, -1)} title="Move the selected rule up one place">
                  <span className="tb-i tb-i-blue">▲</span> Move Up
                </button>
                <button type="button" className="ca-btn"
                  disabled={!canWrite || !selectedRule || selectedRule.sortOrder === rules.length}
                  onClick={() => selectedRule && void move(selectedRule, 1)} title="Move the selected rule down one place">
                  <span className="tb-i tb-i-blue">▼</span> Move Down
                </button>
              </>
            )}
            onNew={() => openDetail(null)}
            onEdit={() => selectedRule && openDetail(selectedRule.id)}
            editDisabled={!selectedRule}
            onDelete={() => selectedRule && void requestDelete(selectedRule)}
            deleteDisabled={!selectedRule}
            onRefresh={() => void load()}
            refreshing={loading}
            exportData={{ filename: 'Permit Row Colors', ...exp }}
            perm={perm}
          />
        </div>

        <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />

        <div className="prc-cols">
          <div className="prc-main">
            <AppDataGrid
              rows={shown}
              columns={columns}
              getRowId={(r) => r.id}
              loading={loading}
              selectable
              selectedIds={selected}
              onSelectionChange={onSelection}
              onRowDoubleClick={(r) => openDetail(r.id)}
              emptyText={search
                ? 'No results match your search.'
                : 'No colour rules — result rows keep their normal colour. Click New to add one.'}
              exportFilename="Permit Row Colors"
              hideHeaderTools
              className="prc-nozebra"
            />
          </div>

          {/* Below the rules, the same layout as the New / Edit page (owner request 2026-10-23). */}
          <SectionPanel title="Preview" icon="ti-eye" note={previewNote} className="prc-preview-panel prc-detail-preview">
            <div className="prc-preview-split">
            <AppDataGrid
              rows={pageRows}
              columns={PREVIEW_COLUMNS}
              getRowId={(r) => r.id}
              getRowClassName={previewRowClass}
              exportFilename="Permit Row Colors Preview"
              emptyText="The rule names no limit type, so there is nothing to preview."
              defaultPageSize={PREVIEW_PAGE_SIZE}
              hideHeaderTools
              hideFooter
              className="prc-nozebra"
            />
            {/* The example permit + "Try a result" (owner requests 2026-10-21 / 2026-10-23), validated
                inline and evaluated live — the SAME component the New / Edit page shows. */}
            <ExamplePermitControls rules={previewSet} fields={previewFields} example={example}
              onExampleChange={setExample} tryValue={tryValue} onTryChange={setTryValue} vertical />
            </div>
          </SectionPanel>
        </div>
      </div>
    </div>
  );
}
